import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Fuel, IndianRupee, Leaf, Sprout, ChevronDown, Calculator as CalcIcon } from "lucide-react";
import { Reveal, Overline, CTAButton, AnimatedNumber } from "@/components/primitives";
import { SpotlightCard } from "@/components/fancy";
import { submitForm } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const SCALES = {
  india: { label: "India (national)", petrol: 3.9e10 },
  state: { label: "A state", petrol: 2.0e9 },
  company: { label: "My company", petrol: null },
};
const BLENDS = { E10: 0.1, E20: 0.2, E30: 0.3 };
const LEAD_ROLES = ["Producer", "OMC", "Investor", "Technology provider", "Government", "Student", "Other"];

const fmtCrore = (n) => (n / 1e7).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function CalculatorTool() {
  const [scale, setScale] = useState("india");
  const [blend, setBlend] = useState("E20");
  const [companyEthanol, setCompanyEthanol] = useState(50000000);
  const [advOpen, setAdvOpen] = useState(false);
  const [petrolPrice, setPetrolPrice] = useState(55);
  const [ethanolPrice, setEthanolPrice] = useState(60);
  const [co2Factor, setCo2Factor] = useState(1.9);

  const [email, setEmail] = useState("");
  const [leadRole, setLeadRole] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const results = useMemo(() => {
    let ethanolLitres;
    if (scale === "company") {
      ethanolLitres = Number(companyEthanol) || 0;
    } else {
      ethanolLitres = SCALES[scale].petrol * BLENDS[blend];
    }
    const petrolDisplaced = ethanolLitres;
    const importSaved = petrolDisplaced * petrolPrice;
    const co2Tonnes = (ethanolLitres * co2Factor) / 1000;
    const farmerIncome = ethanolLitres * ethanolPrice;
    return { petrolDisplaced, importSaved, co2Tonnes, farmerIncome };
  }, [scale, blend, companyEthanol, petrolPrice, ethanolPrice, co2Factor]);

  const handleLead = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await submitForm({
        form_type: "calculator_report",
        email,
        role: leadRole,
        extra: { scale: SCALES[scale].label, blend, petrol_displaced_litres: Math.round(results.petrolDisplaced), import_saved_cr: fmtCrore(results.importSaved) },
      });
      toast.success("Sent. Your full report is on the way.");
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const CARDS = [
    { icon: Fuel, label: "Petrol displaced", value: results.petrolDisplaced / 1e7, suffix: " cr L", color: "text-sky-300" },
    { icon: IndianRupee, label: "Import bill saved", value: results.importSaved / 1e7, prefix: "₹", suffix: " cr", color: "text-emerald-300" },
    { icon: Leaf, label: "CO₂ avoided", value: results.co2Tonnes, suffix: " t", color: "text-lime-300" },
    { icon: Sprout, label: "Farmer & rural income", value: results.farmerIncome / 1e7, prefix: "₹", suffix: " cr", color: "text-amber-300" },
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      {/* Inputs */}
      <Reveal className="lg:col-span-5">
        <div className="rounded-3xl border border-border bg-white p-6 shadow-sm md:p-7">
          <div className="flex items-center gap-2 text-emeraldDeep">
            <CalcIcon className="h-5 w-5" />
            <h3 className="font-display text-lg font-semibold text-navy">Set your inputs</h3>
          </div>

          <div className="mt-6">
            <label className="text-xs font-semibold uppercase tracking-wider text-navy/50">Scale</label>
            <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-parchment p-1">
              {Object.entries(SCALES).map(([k, v]) => (
                <button
                  key={k}
                  data-testid={`scale-${k}`}
                  onClick={() => setScale(k)}
                  className={cn("rounded-lg px-2 py-2 text-xs font-semibold transition-all", scale === k ? "bg-navy text-white shadow" : "text-navy/60 hover:text-navy")}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {scale === "company" ? (
            <div className="mt-6">
              <label className="text-xs font-semibold uppercase tracking-wider text-navy/50">Ethanol produced / blended (litres per year)</label>
              <input
                data-testid="company-ethanol"
                type="number"
                value={companyEthanol}
                onChange={(e) => setCompanyEthanol(e.target.value)}
                className="mt-2 w-full rounded-xl border border-border bg-white px-4 py-3 text-navy outline-none focus:border-emerald2"
              />
            </div>
          ) : (
            <div className="mt-6">
              <label className="text-xs font-semibold uppercase tracking-wider text-navy/50">Blend level</label>
              <div className="mt-2 flex gap-2">
                {Object.keys(BLENDS).map((b) => (
                  <button
                    key={b}
                    data-testid={`blend-${b}`}
                    onClick={() => setBlend(b)}
                    className={cn("flex-1 rounded-xl border-2 py-3 font-display text-lg font-semibold transition-all", blend === b ? "border-emerald2 bg-emerald2/10 text-emeraldDeep" : "border-border text-navy/40 hover:border-emerald2/40")}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 border-t border-border pt-4">
            <button
              data-testid="adv-toggle"
              onClick={() => setAdvOpen((v) => !v)}
              className="flex w-full items-center justify-between text-sm font-semibold text-navy/70 hover:text-navy"
            >
              Adjust assumptions
              <ChevronDown className={cn("h-4 w-4 transition-transform", advOpen && "rotate-180")} />
            </button>
            <AnimatePresence>
              {advOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <div className="space-y-4 pt-4">
                    {[
                      { label: "Landed petrol price ₹/litre", value: petrolPrice, set: setPetrolPrice, id: "petrol-price" },
                      { label: "Ethanol price to producer ₹/litre", value: ethanolPrice, set: setEthanolPrice, id: "ethanol-price" },
                      { label: "CO₂ saved per litre ethanol, kg", value: co2Factor, set: setCo2Factor, id: "co2-factor", step: 0.1 },
                    ].map((f) => (
                      <div key={f.id}>
                        <label className="text-xs font-medium text-navy/60">{f.label}</label>
                        <input
                          data-testid={f.id}
                          type="number"
                          step={f.step || 1}
                          value={f.value}
                          onChange={(e) => f.set(Number(e.target.value))}
                          className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-navy outline-none focus:border-emerald2"
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Reveal>

      {/* Results */}
      <div className="lg:col-span-7">
        <div className="grid gap-4 sm:grid-cols-2">
          {CARDS.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.05}>
              <SpotlightCard glow="rgba(255,255,255,0.10)" className="h-full rounded-2xl border border-white/10 bg-navy grain p-5 transition-transform hover:-translate-y-1">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10"><c.icon className={cn("h-4 w-4", c.color)} /></span>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{c.label}</p>
                </div>
                <p className={cn("mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl", c.color)}>
                  <AnimatedNumber value={c.value} prefix={c.prefix || ""} suffix={c.suffix} />
                </p>
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-navy/45">
          Estimates only — directional coefficients based on Ministry of Petroleum &amp; Natural Gas / PIB figures and standard lifecycle assumptions. Energy-density differences mean petrol displacement is not 1:1 by energy. Adjust the assumptions to model your own case.
        </p>

        {/* Compact report capture */}
        <Reveal className="mt-4">
          <div className="rounded-2xl border border-emerald2/30 bg-emerald2/[0.05] p-5">
            {sent ? (
              <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
                <p className="text-sm font-medium text-navy">Your report is on the way — see the full picture live at the forum.</p>
                <div className="flex shrink-0 gap-2">
                  <CTAButton to="/register" className="text-sm">Register</CTAButton>
                  <CTAButton to="/sponsor" variant="outline" className="text-navy text-sm">Sponsor</CTAButton>
                </div>
              </div>
            ) : (
              <form onSubmit={handleLead} className="space-y-3">
                <p className="font-display text-sm font-semibold text-navy">Get the full report — by feedstock, by state &amp; the 2030 projection.</p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input data-testid="lead-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-navy outline-none focus:border-emerald2 sm:flex-1" />
                  <select data-testid="lead-role" value={leadRole} onChange={(e) => setLeadRole(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm text-navy outline-none focus:border-emerald2 sm:w-36">
                    <option value="">I am a…</option>
                    {LEAD_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button data-testid="lead-submit" type="submit" disabled={loading} className="shrink-0 rounded-xl bg-ethanolOrange px-5 py-2.5 text-sm font-bold text-navy transition-all hover:-translate-y-0.5 hover:bg-orangeDeep disabled:opacity-60">
                    {loading ? "Sending…" : "Email me the report"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
